import React, { useMemo, useEffect } from 'react';
import { debounce } from 'lodash';

import { Form, Input } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useKimLhc } from '@/context/KimLhc';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useUpdateStepAndCommentKimLhcReport } from '@/hooks/useUpdateStepAndCommentKimLhcReport';

import type { NamePath } from 'antd/es/form/interface';
import * as S from '@/components/views/Report/KimLhc/Report/Steps/Result/styles';

interface CommentFieldProps {
	fieldName: NamePath;
	comment?: string | null;
}

const DEBOUNCE_MS = 1000;
const { useWatch } = Form;
const { TextArea } = Input;

export function CommentField({ fieldName, comment }: Readonly<CommentFieldProps>): JSX.Element {
	const { report_id, file_id, form } = useKimLhc();
	const { organization, company } = useApplicationContext();
	const { mutateAsync: updateComment } = useUpdateStepAndCommentKimLhcReport();

	const value = useWatch(fieldName, form) ?? '';

	useEffect(() => {
		form.setFieldValue(fieldName, comment);
	}, [comment]);

	const debouncedUpdateComment = useMemo(
		() =>
			debounce(async (commentValue: string) => {
				if (!report_id || !file_id) return;
				await updateComment({
					id: report_id,
					file_id,
					company_id: company.id,
					organization_id: organization.id,
					comment: commentValue ?? ''
				});
			}, DEBOUNCE_MS),
		[report_id, file_id, company.id, organization.id, updateComment]
	);

	function handleOnChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
		const { value } = e.target;
		form.setFieldValue(fieldName, value);
		debouncedUpdateComment(value);
	}

	return (
		<S.CommentContainer>
			<S.CommentTitle>{I18n.get('Comment')}</S.CommentTitle>
			<Form.Item name={fieldName} noStyle>
				<TextArea
					rows={4}
					showCount
					value={value}
					maxLength={500}
					onChange={handleOnChange}
					placeholder={I18n.get('Add your comments here')}
					style={{ resize: 'none' }}
				/>
			</Form.Item>
		</S.CommentContainer>
	);
}
